// Creator picks — the #/admin panel (campaign T57 WP0-B, contract §3).
//
// Three blocks, in the order John works in them: the creators he knows, the
// pairing form, and what he has paired lately. No Meta credential, no oEmbed, no
// network call to Instagram (D-10) — the only thing that reaches this panel from
// Instagram is a URL on his clipboard.
//
// MOUNTING. `renderAdmin` calls this once, after its own panels and after its
// own `requireSuperAdmin()` has already returned — so this file deliberately
// does NOT re-run the guard: it would be a second round trip to say what the
// caller has just established, and a second place to get the fail-closed
// behaviour wrong. The guard is a UX gate regardless; every write behind it is
// authorised server-side by RLS and by `public.is_super_admin()`.
//
// The brief allows exactly ONE insertion in `admin.ts`, so everything that makes
// this look like a fourth tab lives here: the tab button is injected into the
// existing `.ad-tabs` nav and given its own listener. That works without
// touching admin.ts's own handler because that handler re-queries `.ad-tab` and
// `.ad-panel` at CLICK time, so it sweeps this panel's state correctly even
// though it never bound to this button. If the nav is ever restructured away,
// the fallback below renders the panel as a plain always-visible section rather
// than silently hiding it.
//
// `esc()` is local, as it is in admin.ts, edit.ts, claim.ts, substrate.ts,
// menu-ui.ts and per-type-ui.ts. Six copies is the house pattern here; hoisting
// it into a shared module would be a seventh file and is not in this lane.

import {
  listCreators,
  addCreator,
  searchVenues,
  addPick,
  listRecentPicks,
  deactivatePick,
  reactivatePick,
  repairCreator,
  EXCERPT_MAX,
  type CreatorRow,
  type VenueRow,
  type RecentPick,
} from '../lib/creator-picks-api';
import { canonicalPostUrl, displayHandle } from '../lib/handles';
import { creatorInvariantBreach, CREATOR_INVARIANT_BADGE } from '../lib/creator-invariant';

const HTML_ESCAPES: Record<string, string> = {
  '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;',
};
function esc(s: string | null | undefined): string {
  return (s ?? '').replace(/[&<>"']/g, (c) => HTML_ESCAPES[c] ?? c);
}

/**
 * Only ever link to a canonical Instagram permalink.
 *
 * The trigger guarantees the stored shape, so this is belt-and-braces against a
 * row written before the trigger existed or by some future path that bypasses
 * it — a `javascript:` href in an operator's admin page is not a risk worth
 * carrying for the sake of one `<a>`.
 */
function safePermalink(url: string): string | null {
  return /^https:\/\/www\.instagram\.com\/(p|reel)\/[A-Za-z0-9_-]{5,64}\/$/.test(url) ? url : null;
}

function shortDate(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleDateString();
}

/**
 * What to render for a stored Instagram handle.
 *
 * A handle that is present but that `normaliseHandle` refuses is NOT "no
 * handle": the RPC feeding Curate lowercases and strips `@` with no
 * character-class gate, so Curate would render it and a traveller would see it.
 * Showing "no handle — shown by name" here would be this console lying about
 * the consumer surface, so the raw value is shown and flagged instead.
 */
function handleCell(stored: string | null | undefined): string {
  const raw = (stored ?? '').trim();
  if (raw === '') return '<span class="ad-muted">no handle — shown by name</span>';
  const shown = displayHandle(raw);
  if (shown) return esc(shown);
  return `<span title="Stored as-is. Curate will show it lowercased without the @; `
    + `this console will not tidy it. Fix it on the profile if it is wrong.">`
    + `${esc(raw)} ⚠</span>`;
}

export async function renderCreatorPicks(container: HTMLElement): Promise<void> {
  // Mount ONLY into a live admin shell, and only once.
  //
  // The router wipes #app and re-renders on every hashchange, but `renderAdmin`
  // is async: if John navigates away while its loads are in flight, the stale
  // call still arrives here with a container that now holds a DIFFERENT route.
  // The old `?? container` fallback would then graft a working Pair-a-post form
  // onto #/edit. Requiring the admin shell turns that race into a no-op, and the
  // second test makes a re-entrant mount idempotent rather than additive.
  const host = container.querySelector<HTMLElement>('main.ad-wrap');
  if (!host || host.querySelector('#ad-creator-picks')) return;
  const tabs = container.querySelector<HTMLElement>('.ad-tabs');

  const section = document.createElement('section');
  section.id = 'ad-creator-picks';
  // Without the tab nav there is nothing to reveal the panel, so it stays open.
  section.className = tabs ? 'ad-panel' : 'ad-panel ad-show';
  section.innerHTML = `
    <div class="field-section">
      <h2 class="field-section-title">Creators</h2>
      <p class="ad-muted">Influencer profiles. Created unclaimed and excluded from Saoirse
        selection, so a creator is never offered to a traveller as a place to go. They can
        claim the profile later through the ordinary claim flow.</p>
      <div id="cp-creators">Loading…</div>
      <form id="cp-creator-form" autocomplete="off">
        <label for="cp-creator-name">Display name</label>
        <input type="text" id="cp-creator-name" placeholder="Florence Foodie" required />
        <label for="cp-creator-handle">Instagram handle or profile URL <span class="ad-muted">(optional)</span></label>
        <input type="text" id="cp-creator-handle" placeholder="@florencefoodie" />
        <p class="ad-muted" id="cp-creator-preview" aria-live="polite"></p>
        <button type="submit">Add creator</button>
      </form>
    </div>

    <div class="field-section">
      <h2 class="field-section-title">Pair a post</h2>
      <form id="cp-pair-form" autocomplete="off">
        <label for="cp-post-url">Instagram post URL</label>
        <input type="text" id="cp-post-url" placeholder="https://www.instagram.com/p/C9xYz12AbCd/" required />
        <p class="ad-muted" id="cp-url-preview" aria-live="polite"></p>

        <label for="cp-venue-q">Venue</label>
        <input type="text" id="cp-venue-q" placeholder="Search active providers by name…" />
        <div id="cp-venue-results"></div>
        <p class="ad-muted" id="cp-venue-chosen" aria-live="polite">No venue chosen.</p>

        <label for="cp-creator-select">Creator</label>
        <select id="cp-creator-select"></select>
        <p class="ad-muted">
          <button type="button" class="link" id="cp-jump-add">+ Add a creator first</button>
        </p>

        <label for="cp-excerpt">What they recommended</label>
        <textarea id="cp-excerpt" rows="2" maxlength="${EXCERPT_MAX}"
          placeholder="The lampredotto here is the one she tells everyone to try."></textarea>
        <p class="ad-muted" id="cp-excerpt-count" aria-live="polite"></p>

        <label for="cp-note">Private note <span class="ad-muted">(operator only, never shown to travellers)</span></label>
        <input type="text" id="cp-note" placeholder="Reason for pairing, who to credit, anything." />
        <p class="ad-muted">The note is write-only from here: the database does not let this
          console read it back, so Recent picks below will not show it.</p>

        <button type="submit" id="cp-pair-submit">Pair this post</button>
      </form>
    </div>

    <div class="field-section">
      <h2 class="field-section-title">Recent picks</h2>
      <p class="ad-muted">The last 50, active and inactive. Deactivating takes a pick off the
        traveller's screen and keeps it here as history — nothing is ever hard deleted.</p>
      <div id="cp-picks">Loading…</div>
    </div>

    <p id="cp-status" class="status" aria-live="polite"></p>
  `;

  const anchor = host.querySelector<HTMLElement>('#ad-status');
  if (anchor) host.insertBefore(section, anchor);
  else host.appendChild(section);

  if (tabs) {
    const tab = document.createElement('button');
    tab.className = 'ad-tab';
    tab.type = 'button';
    tab.setAttribute('role', 'tab');
    tab.dataset.panel = 'creator-picks';
    tab.textContent = 'Creator picks';
    tabs.appendChild(tab);
    tab.addEventListener('click', () => {
      container.querySelectorAll('.ad-tab').forEach((t) => t.classList.remove('ad-on'));
      container.querySelectorAll('.ad-panel').forEach((p) => p.classList.remove('ad-show'));
      tab.classList.add('ad-on');
      section.classList.add('ad-show');
    });
  }

  const q = <T extends HTMLElement>(sel: string): T => section.querySelector<T>(sel)!;
  const status = q<HTMLElement>('#cp-status');
  const setStatus = (msg: string, kind: '' | 'success' | 'error'): void => {
    status.textContent = msg;
    status.className = kind ? `status status-${kind}` : 'status';
  };

  /** Surface the failure verbatim — the house rule is that we never swallow. */
  const fail = (err: unknown, fallback: string): void =>
    setStatus(err instanceof Error ? err.message : fallback, 'error');

  let creators: CreatorRow[] = [];
  let chosenVenue: VenueRow | null = null;

  // ── Creators ──────────────────────────────────────────────────────────────

  const renderCreators = (): void => {
    q<HTMLElement>('#cp-creators').innerHTML = creators.length === 0
      ? '<p class="ad-muted">No creators yet. Add the first one below.</p>'
      : `<div class="ad-tablewrap"><table class="ad-table">
          <thead><tr><th>Name</th><th>Handle</th><th>Saoirse</th><th></th></tr></thead>
          <tbody>${creators.map((c) => {
            // R2/G-2(a): badge any influencer row that breaks the contract §1.2
            // invariant — saoirse_excluded not true, OR coordinates set. Nothing
            // in the database enforces this in WP0 (the guarantee is deferred to
            // a WP1 HELD migration), and #/apply can still mint an influencer
            // row without it, so the console is the only thing that catches one.
            const breach = creatorInvariantBreach(c);
            return `
            <tr${c.is_active === false ? ' class="ad-inactive"' : ''}>
              <td>${esc(c.business_name)}${c.is_active === false ? ' <em>(inactive)</em>' : ''}</td>
              <td>${handleCell(c.instagram_handle)}</td>
              <td>${breach
                    // `.ad-missing` is muted grey — quieter than the all-clear
                    // dot beside it — so the error token is applied inline
                    // (global.css is out of lane).
                    ? `<strong style="color: var(--color-error)" title="${esc(breach.reasons.join('; '))}">${
                        esc(CREATOR_INVARIANT_BADGE)}</strong>`
                    : '<span class="ad-y" aria-label="excluded from tours">●</span>'}</td>
              <td>${breach
                    ? `<button type="button" class="ad-mini" data-repair="${esc(c.id)}"
                         title="Sets saoirse_excluded = true and clears any coordinates">Fix</button>`
                    : ''}</td>
            </tr>`;
          }).join('')}</tbody>
        </table></div>`;

    q<HTMLElement>('#cp-creators').querySelectorAll<HTMLButtonElement>('button[data-repair]')
      .forEach((btn) => btn.addEventListener('click', async () => {
        const id = btn.dataset.repair!;
        btn.disabled = true;
        setStatus('Repairing…', '');
        try {
          await repairCreator(id);
          // The success toast is suppressed if the reload failed, for the same
          // reason as everywhere else on this panel: a green message must never
          // be painted over the server's explanation of what went wrong.
          if (await loadCreators()) {
            setStatus('Creator excluded from tours and coordinates cleared.', 'success');
          }
        } catch (err) {
          btn.disabled = false;
          fail(err, 'Could not repair that creator.');
        }
      }));

    const select = q<HTMLSelectElement>('#cp-creator-select');
    const keep = select.value;
    select.innerHTML = creators.length === 0
      ? '<option value="">No creators yet — add one above</option>'
      : `<option value="">Choose a creator…</option>${creators.map((c) => {
          const handle = displayHandle(c.instagram_handle);
          // Inactive is marked here as well as in the table above: an inactive
          // creator can still be paired (nothing forbids it), so the operator
          // should be able to see what he is choosing without scrolling back.
          return `<option value="${esc(c.id)}">${esc(c.business_name)}${
            c.is_active === false ? ' (inactive)' : ''}${
            handle ? ` · ${esc(handle)}` : ''}</option>`;
        }).join('')}`;
    if (keep) select.value = keep;
  };

  /**
   * Returns false if the refresh failed, so callers can avoid announcing an
   * unqualified success over it. A reload that fails right after a write that
   * succeeded is exactly the migration-apply-window symptom this panel has to
   * be able to report, and a green toast used to overwrite the server's message
   * in the same turn.
   */
  const loadCreators = async (): Promise<boolean> => {
    try {
      creators = await listCreators();
      renderCreators();
      return true;
    } catch (err) {
      q<HTMLElement>('#cp-creators').innerHTML = '<p class="ad-muted">Could not load creators.</p>';
      fail(err, 'Could not load creators.');
      return false;
    }
  };

  // Show John the handle we are actually going to store, before he submits it.
  // A silent normalisation ("Florence Foodie" -> "florencefoodie") is the kind of
  // thing that only surfaces later as a dead @name on a traveller's screen.
  const handleInput = q<HTMLInputElement>('#cp-creator-handle');
  const handlePreview = q<HTMLElement>('#cp-creator-preview');
  handleInput.addEventListener('input', () => {
    const raw = handleInput.value.trim();
    if (raw === '') {
      handlePreview.textContent = 'No handle: this creator will be shown by name.';
      return;
    }
    const shown = displayHandle(raw);
    handlePreview.textContent = shown
      ? `Will be stored as ${shown}`
      : 'That is not a handle I can read — use florencefoodie, or paste the profile URL.';
  });

  q<HTMLFormElement>('#cp-creator-form').addEventListener('submit', async (event) => {
    event.preventDefault();
    const button = q<HTMLButtonElement>('#cp-creator-form button[type="submit"]');
    const nameInput = q<HTMLInputElement>('#cp-creator-name');
    button.disabled = true;
    setStatus('Adding the creator…', '');
    try {
      const created = await addCreator({ name: nameInput.value, handle: handleInput.value });
      const reloaded = await loadCreators();
      // Land John where he was heading: the new creator, selected in the form.
      q<HTMLSelectElement>('#cp-creator-select').value = created.id;
      nameInput.value = '';
      handleInput.value = '';
      handlePreview.textContent = '';
      // The write succeeded either way — but if the reload did not, loadCreators
      // has already put the server's message on the status line and it must not
      // be painted over with a green one.
      if (reloaded) setStatus(`Added ${created.business_name}.`, 'success');
    } catch (err) {
      fail(err, 'Could not add that creator.');
    } finally {
      button.disabled = false;
    }
  });

  q<HTMLButtonElement>('#cp-jump-add').addEventListener('click', () => {
    const nameInput = q<HTMLInputElement>('#cp-creator-name');
    nameInput.scrollIntoView({ block: 'center' });
    nameInput.focus();
  });

  // ── Pair a post ───────────────────────────────────────────────────────────

  const urlInput = q<HTMLInputElement>('#cp-post-url');
  const urlPreview = q<HTMLElement>('#cp-url-preview');
  urlInput.addEventListener('input', () => {
    // The RAW value goes to the validator, never a JS-trimmed copy. `trim()`
    // strips the whole Unicode whitespace set; `canonicalPostUrl` strips the
    // four bytes Postgres strips. Trimming here made the preview accept strings
    // the submit then refused — the preview promised a canonical URL and the
    // form answered with a message whose stated cause was not the cause. The
    // emptiness test gets its own trimmed copy; the validator does not.
    if (urlInput.value.trim() === '') { urlPreview.textContent = ''; return; }
    const result = canonicalPostUrl(urlInput.value);
    // The canonical form is shown because it is what gets STORED, and because
    // "already paired to this venue" only makes sense against this spelling.
    urlPreview.textContent = result.ok
      ? `Will be stored as ${result.url}`
      : `${result.reason} ${result.hint}`;
  });

  const excerpt = q<HTMLTextAreaElement>('#cp-excerpt');
  const excerptCount = q<HTMLElement>('#cp-excerpt-count');
  const updateCount = (): void => {
    excerptCount.textContent = `${excerpt.value.trim().length} / ${EXCERPT_MAX}`;
  };
  excerpt.addEventListener('input', updateCount);
  updateCount();

  const renderVenueChoice = (): void => {
    q<HTMLElement>('#cp-venue-chosen').innerHTML = chosenVenue
      ? `Venue: <strong>${esc(chosenVenue.business_name)}</strong> `
        + '<button type="button" class="link" id="cp-venue-clear">change</button>'
      : 'No venue chosen.';
    section.querySelector<HTMLButtonElement>('#cp-venue-clear')?.addEventListener('click', () => {
      chosenVenue = null;
      renderVenueChoice();
    });
  };
  renderVenueChoice();

  const venueResults = q<HTMLElement>('#cp-venue-results');
  let debounce: number | undefined;
  let searchSeq = 0;

  const renderVenues = (rows: VenueRow[], term: string): void => {
    if (term.trim().length < 2) { venueResults.innerHTML = ''; return; }
    venueResults.innerHTML = rows.length === 0
      ? '<p class="ad-muted">No active non-creator providers match. Creators are filtered out '
        + 'of this list on purpose — a creator must never be paired as the venue.</p>'
      : `<ul class="ad-apps">${rows.map((r) => `
          <li>
            <div>
              <strong>${esc(r.business_name)}</strong>
              <span>${esc(r.business_type) || 'no type'}${
                r.claimed_at ? '' : ' · unclaimed'}</span>
            </div>
            <div class="ad-app-actions">
              <button type="button" class="ad-mini" data-venue="${esc(r.id)}">Choose</button>
            </div>
          </li>`).join('')}</ul>`;

    venueResults.querySelectorAll<HTMLButtonElement>('button[data-venue]').forEach((btn) => {
      btn.addEventListener('click', () => {
        chosenVenue = rows.find((r) => r.id === btn.dataset.venue) ?? null;
        renderVenueChoice();
        venueResults.innerHTML = '';
        q<HTMLInputElement>('#cp-venue-q').value = '';
        // Retire any search still in flight, and any timer not yet fired, so a
        // late answer cannot repaint a candidate list under the venue just chosen.
        window.clearTimeout(debounce);
        searchSeq += 1;
      });
    });
  };

  // The debounce cancels a pending REQUEST, never an in-flight RESPONSE, so a
  // slow earlier search could repaint the list after a newer one — or after a
  // venue had already been chosen and the list cleared, putting a stale list of
  // candidates back under the operator's cursor next to the wrong chosen name.
  // A sequence token drops any answer that is no longer the live question.
  q<HTMLInputElement>('#cp-venue-q').addEventListener('input', (event) => {
    const term = (event.target as HTMLInputElement).value;
    window.clearTimeout(debounce);
    debounce = window.setTimeout(() => {
      void (async (): Promise<void> => {
        const mine = ++searchSeq;
        try {
          const rows = await searchVenues(term);
          if (mine !== searchSeq) return;   // a newer search has since been asked
          renderVenues(rows, term);
        } catch (err) {
          if (mine !== searchSeq) return;
          fail(err, 'Could not search providers.');
        }
      })();
    }, 220);
  });

  q<HTMLFormElement>('#cp-pair-form').addEventListener('submit', async (event) => {
    event.preventDefault();
    const button = q<HTMLButtonElement>('#cp-pair-submit');
    button.disabled = true;
    setStatus('Pairing…', '');
    try {
      await addPick({
        postUrl: urlInput.value,
        providerId: chosenVenue?.id ?? '',
        creatorId: q<HTMLSelectElement>('#cp-creator-select').value,
        excerpt: excerpt.value,
        note: q<HTMLInputElement>('#cp-note').value,
      });
      const venueName = chosenVenue?.business_name ?? 'the venue';
      urlInput.value = '';
      urlPreview.textContent = '';
      excerpt.value = '';
      q<HTMLInputElement>('#cp-note').value = '';
      chosenVenue = null;
      renderVenueChoice();
      updateCount();
      if (await loadPicks()) setStatus(`Paired to ${venueName}.`, 'success');
    } catch (err) {
      fail(err, 'Could not pair that post.');
    } finally {
      button.disabled = false;
    }
  });

  // ── Recent picks ──────────────────────────────────────────────────────────

  const renderPicks = (rows: RecentPick[]): void => {
    if (rows.length === 0) {
      q<HTMLElement>('#cp-picks').innerHTML = '<p class="ad-muted">Nothing paired yet.</p>';
      return;
    }
    q<HTMLElement>('#cp-picks').innerHTML = `
      <div class="ad-tablewrap"><table class="ad-table">
        <thead><tr>
          <th>Venue</th><th>Creator</th><th>What they recommended</th>
          <th>Post</th><th>Added</th><th></th>
        </tr></thead>
        <tbody>${rows.map((r) => {
          const link = safePermalink(r.source_url);
          // R1 / WP0A §9: a creator may have no handle; the name is the fallback,
          // and it is always shown, so a missing handle costs nothing here.
          const handle = (r.creator_handle ?? '').trim();
          return `
          <tr${r.is_active ? '' : ' class="ad-inactive"'}>
            <td>${esc(r.provider_name)}</td>
            <td>${esc(r.creator_name)}${
              handle ? `<div class="ad-muted">${handleCell(handle)}</div>` : ''}</td>
            <td>${esc(r.caption_excerpt) || '<span class="ad-muted">—</span>'}</td>
            <td>${link
              ? `<a href="${esc(link)}" target="_blank" rel="noopener noreferrer">open</a>`
              : `<span class="ad-muted" title="${esc(r.source_url)}">not a permalink</span>`}</td>
            <td>${esc(shortDate(r.created_at))}${r.is_active ? '' : ' <em>(inactive)</em>'}</td>
            <td class="ad-app-actions">${r.is_active
              ? `<button type="button" class="ad-mini ad-danger" data-off="${esc(r.id)}">Deactivate</button>`
              : `<button type="button" class="ad-mini" data-on="${esc(r.id)}">Reactivate</button>`}</td>
          </tr>`;
        }).join('')}</tbody>
      </table></div>`;

    const wire = (attr: 'off' | 'on', run: (id: string) => Promise<void>, done: string): void => {
      q<HTMLElement>('#cp-picks').querySelectorAll<HTMLButtonElement>(`button[data-${attr}]`)
        .forEach((btn) => btn.addEventListener('click', async () => {
          btn.disabled = true;
          try {
            await run(btn.dataset[attr]!);
            if (await loadPicks()) setStatus(done, 'success');
          } catch (err) {
            btn.disabled = false;
            fail(err, 'Could not change that pick.');
          }
        }));
    };
    wire('off', deactivatePick, 'Pick deactivated — it is off the traveller screen and kept here.');
    wire('on', reactivatePick, 'Pick reactivated.');
  };

  /** Returns false if the refresh failed — see loadCreators. */
  const loadPicks = async (): Promise<boolean> => {
    try {
      renderPicks(await listRecentPicks());
      return true;
    } catch (err) {
      q<HTMLElement>('#cp-picks').innerHTML = '<p class="ad-muted">Could not load recent picks.</p>';
      fail(err, 'Could not load recent picks.');
      return false;
    }
  };

  await loadCreators();
  await loadPicks();
}
